/**
 * Organization Context
 *
 * Thin wrapper over TanStack Query's useCurrentOrganization hook.
 * Shares the same ['current-organization'] cache key populated by
 * the root route guard (ensureQueryData), so no duplicate API calls.
 */

import type { Organization } from '@/types/bss';
import { useCurrentOrganization } from '@/hooks/use-bss-organizations';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useAuth } from './AuthContext';

export interface OrganizationContextType {
  organization: Organization | null;
  loading: boolean;
  refetch: () => Promise<void>;
  hasOrganization: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { data, isLoading, refetch } = useCurrentOrganization();

  const organization = isAuthenticated ? (data?.organization ?? null) : null;
  const loading = isAuthenticated ? isLoading : false;

  // Wrap refetch to preserve the () => Promise<void> contract
  const refetchWrapper = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const value = useMemo(
    () => ({
      organization,
      loading,
      refetch: refetchWrapper,
      hasOrganization: !!organization,
    }),
    [organization, loading, refetchWrapper]
  );

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}

export default OrganizationContext;
